import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type { PropostaManejo, PropostaProgramaEmLote } from "@shared/manejo-proposta";
import type { TipoBoletim } from "@shared/boletim-periodo";

export type RespostaAgente =
  | { tipo: "texto"; texto: string }
  | { tipo: "proposta"; proposta: PropostaManejo; capacidadeChave: string }
  | { tipo: "proposta_programa_lote"; propostas: PropostaProgramaEmLote[]; capacidadeChave: string };

export function useEnviarMensagemAgente() {
  return useMutation({
    mutationFn: (texto: string) =>
      apiRequest("POST", "/api/agent/mensagem", { texto }).then(r => r.json()) as Promise<RespostaAgente>,
  });
}

// ─── Boletim (Etapa 1 / Ciclo 1) — prévia no canal `app` ──────────────────────

export interface BlocoBoletim {
  chave: string;
  modulo: string;
  titulo: string;
  texto: string;
  vazio: boolean;
  dados: unknown;
}

export interface BoletimPreview {
  data: string;
  /** Período coberto — no semanal, a seg–sex resolvida a partir da data escolhida. */
  periodo: { tipo: TipoBoletim; inicio: string; fim: string };
  vazio: boolean;
  texto: string;
  blocos: BlocoBoletim[];
}

/**
 * Prévia do boletim de um período. `enabled: false` até o usuário clicar em
 * "Gerar prévia" — a montagem varre colheitas, mangarás, tarefas e operações,
 * não é coisa pra disparar sozinha ao abrir a aba.
 * Usa `fetchJson` (checa `r.ok`): sem isso, um 403/500 viraria "boletim vazio".
 *
 * `tipo` entra na queryKey: sem isso, alternar Diário/Semanal na mesma data
 * devolveria o texto em cache do outro tipo.
 */
export function useBoletimPreview(data: string, tipo: TipoBoletim, enabled: boolean) {
  return useQuery<BoletimPreview>({
    queryKey: ["/api/agent/boletim/preview", data, tipo],
    queryFn: () => fetchJson<BoletimPreview>(
      `/api/agent/boletim/preview?data=${encodeURIComponent(data)}&tipo=${encodeURIComponent(tipo)}`,
    ),
    enabled,
  });
}

// ─── Ciclo 2 — contatos, assinaturas e histórico de envios ────────────────────

export interface ContatoAgente {
  id: string;
  nome: string;
  email: string | null;
  telefone: string | null;
  papel: "gestor" | "operador" | "observador";
  funcionarioId: string | null;
  optinEm: string | null;
  ativo: boolean;
  /** Audiência criada/atualizada junto com o contato — é o que a assinatura liga. */
  agentAudienciaId: string | null;
}

export interface ContatoInput {
  nome: string;
  email: string;
  telefone?: string | null;
  papel: "gestor" | "operador" | "observador";
  funcionarioId?: string | null;
  ativo: boolean;
}

export function useContatosAgente() {
  return useQuery<ContatoAgente[]>({
    queryKey: ["/api/agent/contatos"],
    queryFn: () => fetchJson<ContatoAgente[]>("/api/agent/contatos"),
  });
}

export function useSalvarContatoAgente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: { id?: string; dados: ContatoInput }) =>
      apiRequest(id ? "PATCH" : "POST", id ? `/api/agent/contatos/${id}` : "/api/agent/contatos", dados)
        .then(r => r.json()) as Promise<ContatoAgente>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/agent/contatos"] });
      // A audiência do contato muda junto — a lista de assinaturas mostra o destino.
      qc.invalidateQueries({ queryKey: ["/api/agent/assinaturas"] });
    },
  });
}

/** Duas chaves de WhatsApp — nunca uma genérica (Ciclo 3a, ver AgentCanal em shared/schema.ts). */
export type CanalAgente = "email" | "whatsapp_gzappy" | "whatsapp_cloud";

export interface AssinaturaAgente {
  id: string;
  agentAudienciaId: string;
  audienciaNome: string;
  destino: string;
  capacidade: "boletim.diario" | "boletim.semanal";
  canal: CanalAgente;
  agendaCron: string;
  horario: string | null;
  timezone: string;
  ativo: boolean;
}

export function useAssinaturasAgente() {
  return useQuery<AssinaturaAgente[]>({
    queryKey: ["/api/agent/assinaturas"],
    queryFn: () => fetchJson<AssinaturaAgente[]>("/api/agent/assinaturas"),
  });
}

export function useSalvarAssinaturaAgente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: {
      agentAudienciaId: string;
      capacidade: "boletim.diario";
      canal: CanalAgente;
      horario: string;
      ativo: boolean;
    }) => apiRequest("PUT", "/api/agent/assinaturas", dados).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/agent/assinaturas"] }),
  });
}

// ─── Ciclo 3a — audiências de grupo (WhatsApp) ────────────────────────────────

export interface AudienciaAgente {
  id: string;
  nome: string;
  tipo: "contato" | "grupo";
  destino: string;
  agentContatoId: string | null;
}

/** Todas as audiências da org — contato E grupo. `useContatosAgente` só cobre contato. */
export function useAudienciasAgente() {
  return useQuery<AudienciaAgente[]>({
    queryKey: ["/api/agent/audiencias"],
    queryFn: () => fetchJson<AudienciaAgente[]>("/api/agent/audiencias"),
  });
}

export interface AudienciaGrupoInput {
  nome: string;
  /** JID do grupo (termina em @g.us) — ver painel do GZappy. */
  destino: string;
}

export function useSalvarAudienciaGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: { id?: string; dados: AudienciaGrupoInput }) =>
      apiRequest(id ? "PATCH" : "POST", id ? `/api/agent/audiencias/grupo/${id}` : "/api/agent/audiencias/grupo", dados)
        .then(r => r.json()) as Promise<AudienciaAgente>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/agent/audiencias"] });
      qc.invalidateQueries({ queryKey: ["/api/agent/assinaturas"] });
    },
  });
}

export interface EnvioAgente {
  id: string;
  canal: CanalAgente;
  destino: string;
  capacidade: string | null;
  agentAudienciaId: string | null;
  periodoInicio: string | null;
  periodoFim: string | null;
  status: "enviada" | "falhou" | "pulado_vazio";
  erro: string | null;
  criadoEm: string;
}

export function useEnviosAgente() {
  return useQuery<EnvioAgente[]>({
    queryKey: ["/api/agent/envios"],
    queryFn: () => fetchJson<EnvioAgente[]>("/api/agent/envios"),
  });
}

export interface ResultadoEnvioAgente {
  desfecho: "enviada" | "falhou" | "pulado_vazio" | "ja_enviada";
  destino: string;
  periodo: { tipo: string; inicio: string; fim: string };
  erro?: string;
}

/**
 * ⚠️ ESCRITA REAL — dispara email de verdade, pelo mesmo caminho do cron. Quem
 * chama confirma antes com o usuário. `forcar` só é ligado pelo "reenviar mesmo
 * assim": sem ele, período já enviado devolve `ja_enviada` e nada sai.
 */
export function useEnviarBoletimAgora() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { assinaturaId: string; data?: string | null; tipo?: TipoBoletim; forcar?: boolean }) =>
      apiRequest("POST", "/api/agent/envios", dados).then(r => r.json()) as Promise<ResultadoEnvioAgente>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/agent/envios"] }),
  });
}

// ─── Enviar teste (aba Boletim, a partir da prévia) ──────────────────────────

export interface EnviarTesteInput {
  contatoId: string;
  /** Só os dois canais que entregam de verdade hoje — `whatsapp_cloud` não existe ainda. */
  canal: "email" | "whatsapp_gzappy";
  data: string;
  /** No semanal, `data` é qualquer dia da semana — o servidor resolve a seg–sex dela. */
  tipo: TipoBoletim;
}

export interface ResultadoTesteAgente {
  desfecho: "enviada" | "falhou" | "pulado_vazio" | "ja_enviada";
  destino: string;
  periodo: { tipo: string; inicio: string; fim: string };
  erro?: string;
  providerId?: string;
}

/**
 * ⚠️ ESCRITA REAL — manda o boletim de verdade pro contato escolhido, pelo
 * canal escolhido. Não é idempotente de propósito (capacidade `boletim.teste`
 * fica fora do índice de reserva — migração 038): repetir o mesmo teste manda
 * de novo, nunca devolve `ja_enviada`.
 */
export function useEnviarTeste() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: EnviarTesteInput) =>
      apiRequest("POST", "/api/agent/envios/teste", dados).then(r => r.json()) as Promise<ResultadoTesteAgente>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/agent/envios"] }),
  });
}
